/**
 * ST Portal demo / guest mode.
 * Standalone page showing what the subcontractor portal looks like, with fake
 * sample data and no API calls. Used in admin previews + linkable demo URL.
 */
import React, { useState } from 'react';
import { Buildings, FileText, ChatCircle, CheckCircle, Clock, MapPin, Lock, Eye, Wrench, ArrowSquareOut } from '@phosphor-icons/react';

const DEMO_DATA = {
  consultation: {
    project_name: 'Rénovation immeuble Voltaire',
    project_address: '14 rue Voltaire, 59100 Roubaix',
    company_name: 'BATI-BOYS',
    lot: 'Lot 04 - Plomberie',
    invitation_date: '2026-02-10',
    response_deadline: '2026-02-28',
    status: 'En attente de chiffrage',
    contact_admin: { name: 'Hicham B.', email: 'hicham@bati-boys.fr' },
  },
  lines: [
    { id: 'l1', name: 'Alimentation eau froide cuisine', qty: 1, unit: 'forfait', est_price: '—' },
    { id: 'l2', name: 'Évacuation salle de bain (4 unités)', qty: 4, unit: 'pce', est_price: '—' },
    { id: 'l3', name: 'Pose chaudière condensation', qty: 1, unit: 'pce', est_price: '—' },
    { id: 'l4', name: 'Radiateurs étages (12 unités)', qty: 12, unit: 'pce', est_price: '—' },
  ],
  docs: [
    { name: 'CCTP Plomberie.pdf', size: '1.2 MB', date: '2026-02-10' },
    { name: 'Plans niveau RDC.pdf', size: '3.4 MB', date: '2026-02-10' },
    { name: 'Plans niveau R+1.pdf', size: '3.1 MB', date: '2026-02-10' },
  ],
  messages: [
    { from: 'admin', name: 'Hicham', body: 'Bonjour, voici la consultation pour le lot plomberie. Le délai de réponse court jusqu\'au 28 février.', date: '2026-02-10 09:30' },
    { from: 'admin', name: 'Hicham', body: 'N\'hésitez pas si vous avez des questions techniques sur le CCTP.', date: '2026-02-10 09:32' },
  ],
};

const DemoBadge = () => (
  <div style={{ position: 'fixed', top: 12, right: 12, zIndex: 100, padding: '6px 14px', background: 'linear-gradient(135deg, #FBBF24, #F59E0B)', color: '#451A03', borderRadius: 999, fontSize: '0.78rem', fontWeight: 800, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <Eye size={12} weight="bold" /> MODE INVITÉ — Démo
  </div>
);

const Tab = ({ id, label, icon: Icon, active, onClick }) => (
  <button onClick={() => onClick(id)} data-testid={`st-demo-tab-${id}`} style={{
    padding: '10px 16px', border: 'none', cursor: 'pointer', background: 'transparent',
    fontSize: '0.9rem', fontWeight: 700, borderBottom: active ? '3px solid #7C3AED' : '3px solid transparent',
    color: active ? '#7C3AED' : '#5E5F66', marginBottom: -2, display: 'inline-flex', alignItems: 'center', gap: 6,
  }}>
    <Icon size={15} /> {label}
  </button>
);

const ChiffrageTab = () => {
  const [responses, setResponses] = useState({});
  const setLine = (id, v) => setResponses(prev => ({ ...prev, [id]: v }));
  const total = Object.entries(responses).reduce((acc, [id, v]) => {
    const line = DEMO_DATA.lines.find(l => l.id === id);
    return acc + (parseFloat(v || 0) * (line?.qty || 1));
  }, 0);
  return (
    <div>
      <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px', marginBottom: 14, color: '#92400E', fontSize: '0.85rem' }}>
        <Lock size={14} style={{ verticalAlign: 'middle' }} /> Mode démo : la saisie est désactivée. Aucune donnée n'est envoyée.
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: 8, overflow: 'hidden' }}>
        <thead>
          <tr style={{ background: '#F4F5F7' }}>
            <th style={{ padding: 10, textAlign: 'left', fontSize: '0.78rem', color: '#5E5F66' }}>Désignation</th>
            <th style={{ padding: 10, textAlign: 'center', fontSize: '0.78rem', color: '#5E5F66' }}>Qté</th>
            <th style={{ padding: 10, textAlign: 'center', fontSize: '0.78rem', color: '#5E5F66' }}>Unité</th>
            <th style={{ padding: 10, textAlign: 'right', fontSize: '0.78rem', color: '#5E5F66' }}>Votre prix HT</th>
            <th style={{ padding: 10, textAlign: 'right', fontSize: '0.78rem', color: '#5E5F66' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {DEMO_DATA.lines.map(l => (
            <tr key={l.id} style={{ borderTop: '1px solid #F4F5F7' }}>
              <td style={{ padding: 10, fontSize: '0.88rem' }}>{l.name}</td>
              <td style={{ padding: 10, textAlign: 'center', fontSize: '0.88rem' }}>{l.qty}</td>
              <td style={{ padding: 10, textAlign: 'center', fontSize: '0.88rem' }}>{l.unit}</td>
              <td style={{ padding: 10, textAlign: 'right' }}>
                <input type="number" placeholder="0.00" value={responses[l.id] || ''} disabled
                  onChange={(e) => setLine(l.id, e.target.value)}
                  style={{ width: 100, padding: '4px 8px', border: '1px solid #E2E2E6', borderRadius: 4, textAlign: 'right', background: '#F4F5F7' }} />
                <span style={{ marginLeft: 4, fontSize: '0.78rem', color: '#9CA3AF' }}>€</span>
              </td>
              <td style={{ padding: 10, textAlign: 'right', fontWeight: 600, fontSize: '0.88rem', color: '#9CA3AF' }}>—</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#EDE9FE' }}>
            <td colSpan={4} style={{ padding: 12, textAlign: 'right', fontWeight: 700, color: '#7C3AED' }}>Total HT estimé</td>
            <td style={{ padding: 12, textAlign: 'right', fontWeight: 800, color: '#7C3AED' }}>{total.toFixed(2)} €</td>
          </tr>
        </tfoot>
      </table>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button disabled className="btn btn-secondary btn-sm" style={{ opacity: 0.6 }}>Enregistrer brouillon</button>
        <button disabled className="btn btn-primary btn-sm" style={{ background: '#7C3AED', opacity: 0.6 }}>Soumettre l'offre</button>
      </div>
    </div>
  );
};

const DocsTab = () => (
  <div>
    {DEMO_DATA.docs.map((d, idx) => (
      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'white', border: '1px solid #E2E2E6', borderRadius: 8, marginBottom: 8 }}>
        <FileText size={22} color="#7C3AED" />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{d.name}</div>
          <div style={{ fontSize: '0.74rem', color: '#5E5F66' }}>{d.size} — {d.date}</div>
        </div>
        <button disabled className="btn btn-secondary btn-sm" style={{ opacity: 0.6 }}>Télécharger</button>
      </div>
    ))}
  </div>
);

const MessagesTab = () => (
  <div>
    <div style={{ maxHeight: 280, overflow: 'auto', padding: 12, background: '#F8F9FB', borderRadius: 8 }}>
      {DEMO_DATA.messages.map((m, idx) => (
        <div key={idx} style={{ marginBottom: 12, padding: '10px 14px', background: 'white', borderRadius: 8, border: '1px solid #E2E2E6' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <strong style={{ fontSize: '0.85rem', color: '#7C3AED' }}>{m.name}</strong>
            <span style={{ fontSize: '0.74rem', color: '#9CA3AF' }}>{m.date}</span>
          </div>
          <div style={{ fontSize: '0.88rem', color: '#0A0B10' }}>{m.body}</div>
        </div>
      ))}
    </div>
    <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
      <input disabled placeholder="Mode démo - saisie désactivée" style={{ flex: 1, padding: '8px 12px', border: '1px solid #E2E2E6', borderRadius: 6, fontSize: '0.85rem', background: '#F4F5F7' }} />
      <button disabled className="btn btn-primary btn-sm" style={{ opacity: 0.6, background: '#7C3AED' }}>Envoyer</button>
    </div>
  </div>
);

const STPortalDemoPage = () => {
  const [tab, setTab] = useState('chiffrage');
  const c = DEMO_DATA.consultation;
  return (
    <div data-testid="st-portal-demo" style={{ background: '#F4F5F7', minHeight: '100vh', padding: '0' }}>
      <DemoBadge />
      <div style={{ background: 'linear-gradient(135deg, #7C3AED, #5B21B6)', color: 'white', padding: '20px 28px' }}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          <div style={{ fontSize: '0.78rem', opacity: 0.9, marginBottom: 4, letterSpacing: 1 }}>{c.company_name.toUpperCase()} — PORTAIL SOUS-TRAITANT</div>
          <h1 style={{ margin: 0, fontSize: '1.6rem' }}>{c.project_name}</h1>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 10, fontSize: '0.88rem' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin size={14} /> {c.project_address}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Wrench size={14} /> {c.lot}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={14} /> Délai : {c.response_deadline}</span>
            <span style={{ padding: '2px 10px', background: 'rgba(255,255,255,0.18)', borderRadius: 999, fontWeight: 700 }}>{c.status}</span>
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '18px 28px' }}>
        <div style={{ display: 'flex', gap: 6, borderBottom: '2px solid #E2E2E6', marginBottom: 18, overflowX: 'auto' }}>
          <Tab id="chiffrage" label="Chiffrage" icon={CheckCircle} active={tab === 'chiffrage'} onClick={setTab} />
          <Tab id="docs"      label="Documents" icon={FileText}   active={tab === 'docs'}      onClick={setTab} />
          <Tab id="messages"  label="Messages"  icon={ChatCircle}  active={tab === 'messages'}  onClick={setTab} />
        </div>
        {tab === 'chiffrage' && <ChiffrageTab />}
        {tab === 'docs'      && <DocsTab />}
        {tab === 'messages'  && <MessagesTab />}
        <div style={{ marginTop: 24, padding: '12px 16px', background: 'white', borderRadius: 8, border: '1px solid #E2E2E6', fontSize: '0.82rem', color: '#5E5F66', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Buildings size={16} color="#7C3AED" />
          Pour un vrai accès au portail, demandez à <strong>{c.contact_admin.name}</strong> ({c.contact_admin.email}) de vous envoyer un lien d'invitation par email.
          <a href="/" target="_blank" rel="noreferrer" style={{ marginLeft: 'auto', color: '#7C3AED', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            Retour à BATI-BOYS <ArrowSquareOut size={12} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default STPortalDemoPage;
